import { useState } from "react";
import { ArrowUpRight, Clock } from "lucide-react";
import { useLanguage } from "@/i18n";
import { TOURS } from "@/tours";
import { Reveal, MaskedLine } from "@/components/Reveal";
import TourModal from "@/components/TourModal";

export default function TourGrid({ onRequestTour }) {
  const { lang, t } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <section id="tours" data-testid="tours-section" className="scroll-mt-24 py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="mb-16 lg:mb-24 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#C9A227] mb-5">{t.tours.overline}</p>
            </Reveal>
            <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink">
              <MaskedLine delay={0.1}>{t.tours.title1}</MaskedLine>
              <MaskedLine delay={0.22}>
                <span className="italic text-azure">{t.tours.title2}</span>
              </MaskedLine>
            </h2>
          </div>
          <Reveal delay={0.25} className="lg:col-span-4">
            <p className="text-base lg:text-lg leading-relaxed text-ink/70">{t.tours.sub}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {TOURS.map((tour, i) => {
            const c = tour[lang];
            return (
              <Reveal key={tour.slug} delay={(i % 3) * 0.08} className={tour.span}>
                <button
                  data-testid={`tour-card-${tour.slug}`}
                  onClick={() => setActive(tour)}
                  className="group relative block w-full h-[420px] lg:h-[480px] overflow-hidden rounded-sm text-left"
                >
                  <img
                    src={tour.image}
                    alt={c.title}
                    loading="lazy"
                    style={{ objectPosition: tour.imgPos || "center" }}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent" />
                  <div className="absolute top-5 left-5 flex items-center gap-2">
                    {tour.day && (
                      <span className="rounded-full bg-[#C9A227] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0F2C50]">
                        {t.tours[tour.day]}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-ink/45 backdrop-blur-md px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#FDFBF7]">
                      <Clock className="w-3 h-3" /> {t.tours.fullDay}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-7 lg:p-9">
                    <div className="flex items-end justify-between gap-4">
                      <div>
                        <h3 className="font-serif text-3xl lg:text-4xl font-medium tracking-tight text-[#FDFBF7] mb-2">{c.title}</h3>
                        <p className="text-sm leading-relaxed text-[#F3EFE9]/85 max-w-md line-clamp-2">{c.short}</p>
                      </div>
                      <span className="shrink-0 inline-flex items-center justify-center w-12 h-12 rounded-full border border-[#FDFBF7]/40 text-[#FDFBF7] transition-all duration-500 group-hover:bg-[#C9A227] group-hover:border-[#C9A227] group-hover:text-[#0F2C50] group-hover:rotate-45">
                        <ArrowUpRight className="w-5 h-5" />
                      </span>
                    </div>
                    <p className="mt-4 text-[10px] font-bold uppercase tracking-[0.22em] text-[#FDFBF7]/60 opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
                      {t.tours.open}
                    </p>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>

      <TourModal tour={active} onClose={() => setActive(null)} onRequestTour={onRequestTour} />
    </section>
  );
}
